import React, { useMemo, useRef } from "react";
import CircleBar from "../../../../Components/CircleBar";
import { statuses } from '../../../../Data/Jobs';
import { useNavigate } from "react-router-dom";
import { classNames, toQueryString } from "../../../../Services/helpers";

const Statuses = ({jobs, size, limit, className, searchParams = {}}) => {
    const navigate = useNavigate();

    const _size = size ?? 50;
    const _limit = limit ?? 99;
    const container = useRef(null);

    const [total, order, flattened, other, otherStatuses] = useMemo(() => {
        if (Array.isArray(jobs)) {
            const total = jobs.reduce((acc, job) => acc + job.count, 0);
            let order = jobs.sort((a, b) => b.count - a.count).map(job => job.status);
            const flattened = jobs.reduce((prev, job) => ({[job.status]: job.count, ...prev}), {});
            let other = null;
            let otherStatuses = null;

            if (order.length > _limit) {
                order = order.slice(0, _limit);
                other = jobs.filter(job => !order.includes(job.status)).reduce((acc, job) => acc + job.count, 0);
                otherStatuses = jobs.filter(job => !order.includes(job.status))
                                    .map(job => job.status)
                                    .filter((v, i, a) => a.indexOf(v) === i);
            } else if (order.length === 0) {
                other = 0;
                otherStatuses = [];
            }

            return [total, order, flattened, other, otherStatuses];
        }

        const total = Object.values(jobs).reduce((sum, num) => sum + num, 0);
        let order = Object.keys(jobs).sort((a, b) => jobs[b] - jobs[a]);
        const flattened = jobs;
        let other = null;
        let otherStatuses = null;

        if (order.length > _limit) {
            order = order.slice(0, _limit);
            other = Object.keys(jobs).filter(status => !order.includes(status)).reduce((sum, status) => sum + jobs[status], 0);
            otherStatuses = Object.keys(jobs).filter(status => !order.includes(status));
        } else if (order.length === 0) {
            other = 0;
            otherStatuses = [];
        }

        return [total, order, flattened, other, otherStatuses];
    }, [jobs, _limit]);

    const pickOrder = (className) => {
        // The CCS compiler won't include these classes if we create them dynamically
        const orders = [
            'order-first', 'order-2', 'order-3', 'order-4', 'order-5', 'order-6',
            'order-7', 'order-8', 'order-9', 'order-10', 'order-11', 'order-12'
        ];

        return orders[order.indexOf(className)] ?? 'order-last';
    }

    return <div className={classNames('flex flex-row gap-x-4 snap-x overflow-x-auto w-full pb-2', className)} ref={container}>
        {order.map(status => {
            return <div className={pickOrder(status)}
                key={status}
                onClick={() => navigate(`/plan/jobs?${toQueryString({jobStatuses: [status], ...searchParams})}`)}
            >
                    <CircleBar
                        size={_size}
                        label={statuses[status].shortName}
                        value={flattened[status] ?? 0}
                        total={total}
                        colors={statuses[status].colors}
                    />
            </div>
        })}
        {other !== null && <div className={pickOrder('other')}
                                onClick={() => navigate(`/plan/jobs?${toQueryString({jobStatuses: otherStatuses, ...searchParams})}`)}
                            >
            <CircleBar
                size={_size}
                label="Other"
                value={other}
                total={total}
                colors={'stroke-gray-900 fill-gray-900'}
            />
        </div>}
        <MissingCircles there={order.length} size={_size} container={container?.current} />
    </div>
}

const MissingCircles = ({there, size, container}) => {
    if (!container) {
        return null;
    }

    const need = Math.ceil(container.offsetWidth / (size + parseInt(getComputedStyle(container).fontSize))) - there;
    const circles = [];

    for (let i = there; i < need; i++) {
        circles.push(
            <div key={i} className="order-last">
                <CircleBar size={size} label=" " value={0} total={0} colors={'stroke-gray-200 fill-gray-200'} />
            </div>
        )
    }

    return circles;
}

export default Statuses;
